import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Moment } from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '@app/store/state/app.state';
import Colours from '@shared/ui/atoms/Colours';
import Icon, { IconTypes } from '@shared/ui/atoms/icon/Icon';
import { BodySmall, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { SimpleSpinner } from '@shared/ui/atoms/SimpleSpinner';
import { UserAvatarAndName } from '@shared/ui/molecules/UserAvatarAndName';
import { iconPackPath } from '@shared/utils/WhereaboutsHelper';
import { DATE_FORMAT } from '@shared/utils/DateUtils';
import { IconPack } from '@shared/models/company.models';
import { MemberMovements, WhereaboutsGroupType } from '@features/whereabouts/models/movements.models';
import { OFFICE_OPTION, UNKNOWN_OPTION } from '@features/whereabouts/services/WhereaboutOptions';
import {
  selectFocusedGroupId,
  selectFocusedUserId,
  startEditingWhereabouts,
} from '@features/whereabouts/store/editMovements.duck';
import EditMovementsPanel
  from '@shared/ui/organisms/movements-wallchart/components/edit-movements-panel/EditMovementsPanel';
import { CellInfoHover } from './CellInfoHover';
import { CellSpan, whereaboutsByDate } from './weeklySpans';
import { CellBadges, CheckinConfirmation, ConfirmedTick, useCheckinConfirmations } from './CheckinConfirmations';
import {
  Cell,
  CellIcon,
  CellText,
  DeskLabel,
  MemberWrapper,
  NoteIndicator,
  V2Colours,
  getCellColours,
} from './WhereaboutsV2Styles';

interface Props {
  member: MemberMovements;
  spans: CellSpan[];
  hasAnyWhereabouts: boolean;
  daysInWeek: Moment[];
  officeNameMap: Map<number, string>;
  iconPack: IconPack;
  // Label cells with the whereabouts option (current-user row) instead of the member's name.
  showWhereaboutsLabel?: boolean;
}

// One member's week: hover/status overlays, the merged whereabouts spans, and their edit panel.
// Hover state lives here so mouse movement only re-renders this row.
export function MemberWeekRow({ member, spans, hasAnyWhereabouts, daysInWeek, officeNameMap, iconPack, showWhereaboutsLabel }: Props) {
  const dispatch = useDispatch();
  const [hoveredDayIdx, setHoveredDayIdx] = useState<number | null>(null);
  const isMemberEditing = useSelector((state: AppState) =>
    selectFocusedUserId(state) === member.id && selectFocusedGroupId(state) === WhereaboutsGroupType.Users);

  const byDate = useMemo(() => whereaboutsByDate(member), [member]);
  const checkins = useCheckinConfirmations();
  const dayCount = daysInWeek.length;

  const onDayClick = (dayIdx: number) => {
    const date = daysInWeek[dayIdx];
    dispatch(startEditingWhereabouts({ userId: member.id, date: date.clone(), groupId: WhereaboutsGroupType.Users }));
  };

  const renderSpan = (span: CellSpan) => {
    const confirmations = span.confirmed
      ? daysInWeek.slice(span.startIdx, span.startIdx + span.span)
        .map(day => checkins(member.id, day.format(DATE_FORMAT)))
        .filter((c): c is CheckinConfirmation => !!c)
      : [];
    const tick = confirmations.length > 0 ? <ConfirmedTick confirmations={confirmations} officeNameMap={officeNameMap} /> : null;
    const { bg, border } = getCellColours(span.amOption);
    const officeName = span.amOfficeId ? officeNameMap.get(span.amOfficeId) || '' : '';
    const isUnknown = span.amOption.id === UNKNOWN_OPTION.id && span.pmOption.id === UNKNOWN_OPTION.id;
    const dayClassName = Array.from({ length: span.span }, (_, i) =>
      `wallchartRow__day--${daysInWeek[span.startIdx + i].format('YYYYMMDD')}`
    ).join(' ');
    const spanIsHovered = hoveredDayIdx !== null
      && hoveredDayIdx >= span.startIdx
      && hoveredDayIdx < span.startIdx + span.span;
    const gridColumnStyle = { gridColumn: `${span.startIdx + 1} / span ${span.span}` };

    if (isUnknown) {
      if (hasAnyWhereabouts) return null;
      return (
        <ClickableCell key={span.startIdx}
                       className={dayClassName}
                       style={gridColumnStyle}
                       $bg={bg}
                       $borderColour={border}>
          {!showWhereaboutsLabel && <UserAvatarAndName firstName={member.firstName} lastName={member.lastName} hideName={true} colour="darkBlue" disableHover={true} />}
          <CellText>
            <BodySmall weight={600}>{showWhereaboutsLabel ? span.amOption.label : `${member.firstName} ${member.lastName}`}</BodySmall>
          </CellText>
        </ClickableCell>
      );
    }

    const isSplit = span.amOption.id !== span.pmOption.id;
    const pmColours = getCellColours(span.pmOption);

    const splitInfoNeeded = isSplit && span.infoDates.length > 0;
    if (isSplit) {
      const splitCell = (
        <ClickableSplitCell
          className={dayClassName}
          style={splitInfoNeeded ? undefined : gridColumnStyle}
        >
          <SplitHalf $bg={bg} $borderColour={border}>
            <CellIcon src={iconPackPath(iconPack, span.amOption.image, span.amOption.freeBusyStatus)} alt="" />
            <CellText>
              <BodyVerySmall weight={600}>{showWhereaboutsLabel ? span.amOption.label : `${member.firstName} ${member.lastName}`}</BodyVerySmall>
              {!showWhereaboutsLabel && !span.amOption.required && span.amOption.pickable && <DeskLabel>{span.amOption.label}</DeskLabel>}
              {span.amOptionItemLabel && <DeskLabel>{span.amOptionItemLabel}</DeskLabel>}
            </CellText>
            {span.amOption.id === OFFICE_OPTION.id && tick && <CellBadges>{tick}</CellBadges>}
          </SplitHalf>
          <SplitHalf $bg={pmColours.bg} $borderColour={pmColours.border}>
            <CellIcon src={iconPackPath(iconPack, span.pmOption.image, span.pmOption.freeBusyStatus)} alt="" />
            <CellText>
              <BodyVerySmall weight={600}>{showWhereaboutsLabel ? span.pmOption.label : `${member.firstName} ${member.lastName}`}</BodyVerySmall>
              {!showWhereaboutsLabel && !span.pmOption.required && span.pmOption.pickable && <DeskLabel>{span.pmOption.label}</DeskLabel>}
              {span.pmOptionItemLabel && <DeskLabel>{span.pmOptionItemLabel}</DeskLabel>}
            </CellText>
            {((span.amOption.id !== OFFICE_OPTION.id && tick) || splitInfoNeeded) && (
              <CellBadges>
                {span.amOption.id !== OFFICE_OPTION.id && tick}
                {splitInfoNeeded && <NoteIndicator><img src="/assets/icons/font/Note.svg" alt="" width={16} height={16} /></NoteIndicator>}
              </CellBadges>
            )}
          </SplitHalf>
        </ClickableSplitCell>
      );
      if (!splitInfoNeeded) {
        return <React.Fragment key={span.startIdx}>{splitCell}</React.Fragment>;
      }
      return (
        <CellInfoHover
          key={span.startIdx}
          style={gridColumnStyle}
          trigger={splitCell}
          noteUserId={member.id}
          noteDates={span.infoDates}
          controlledOpen={spanIsHovered}
        />
      );
    }

    const hasNote = span.infoDates.length > 0;
    const cell = (
      <ClickableCell
        className={dayClassName}
        style={hasNote ? undefined : gridColumnStyle}
        $bg={bg}
        $borderColour={border}
      >
        <CellIcon src={iconPackPath(iconPack, span.amOption.image, span.amOption.freeBusyStatus)} alt="" />
        <CellText>
          <BodySmall weight={600}>{showWhereaboutsLabel ? span.amOption.label : `${member.firstName} ${member.lastName}`}</BodySmall>
          {!showWhereaboutsLabel && !span.amOption.required && span.amOption.pickable && <DeskLabel>{span.amOption.label}</DeskLabel>}
          {span.amOptionItemLabel === span.pmOptionItemLabel ?
            (span.amOptionItemLabel && <DeskLabel>{span.amOptionItemLabel}</DeskLabel>) : <>
              {span.amOptionItemLabel && <DeskLabel>AM: {span.amOptionItemLabel}</DeskLabel>}
              {span.pmOptionItemLabel && <DeskLabel>PM: {span.pmOptionItemLabel}</DeskLabel>}
            </>}
          {(officeName || span.deskLabel) && <DeskLabel>{officeName}{officeName && span.deskLabel && ' - '}{span.deskLabel}</DeskLabel>}
          {span.parkingLabel && <DeskLabel>{span.parkingLabel}</DeskLabel>}
        </CellText>
        {(tick || hasNote) && (
          <CellBadges>
            {tick}
            {hasNote && <NoteIndicator><img src="/assets/icons/font/Note.svg" alt="" width={16} height={16} /></NoteIndicator>}
          </CellBadges>
        )}
      </ClickableCell>
    );
    if (!hasNote) return <React.Fragment key={span.startIdx}>{cell}</React.Fragment>;
    return (
      <CellInfoHover
        key={span.startIdx}
        style={gridColumnStyle}
        trigger={cell}
        noteUserId={member.id}
        noteDates={span.infoDates}
        controlledOpen={spanIsHovered}
      />
    );
  };

  return (
    <MemberWrapper onMouseLeave={() => setHoveredDayIdx(null)}>
      <RowContainer>
        <DayHoverOverlay $dayCount={dayCount}>
          {daysInWeek.map((day, i) => (
            <DayHoverCell key={i}
                          className={`wallchartRow__day--${day.format('YYYYMMDD')}`}
                          $hovered={hoveredDayIdx === i}
                          onMouseEnter={() => setHoveredDayIdx(i)}
                          onClick={() => onDayClick(i)}>
              {hoveredDayIdx === i && (
                <AddIconButton>
                  <Icon icon={IconTypes.Add} size="medium" color="white" />
                </AddIconButton>
              )}
            </DayHoverCell>
          ))}
        </DayHoverOverlay>
        <DayStatusOverlay $dayCount={dayCount}>
          {daysInWeek.map((day, i) => {
            const dayData = byDate.get(day.format(DATE_FORMAT));
            const isSaving = !!dayData?.isWhereaboutsSaving;
            const isFailed = !!dayData?.whereaboutsUpdateFailed;
            return (
              <DayStatusCell key={i}>
                {isSaving && <CellLoadingOverlay><SimpleSpinner size={20} /></CellLoadingOverlay>}
                {isFailed && !isSaving && <CellFailedOverlay><Icon icon={IconTypes.Alert} color="red" /></CellFailedOverlay>}
              </DayStatusCell>
            );
          })}
        </DayStatusOverlay>
        <UserRow $dayCount={dayCount}>
          {spans.map(renderSpan)}
        </UserRow>
      </RowContainer>
      {isMemberEditing && <EditMovementsPanel isEditing={isMemberEditing} userId={member.id} />}
    </MemberWrapper>
  );
}

const UserRow = styled.div<{ $dayCount: number }>`
  display: grid;
  grid-template-columns: repeat(${props => props.$dayCount}, 1fr);
  gap: 4px;
  padding: 4px 0;
  position: relative;
  min-height: 50px;
`;

const RowContainer = styled.div`
  position: relative;
`;

const DayHoverOverlay = styled.div<{ $dayCount: number }>`
  position: absolute;
  inset: 4px 0;
  display: grid;
  grid-template-columns: repeat(${props => props.$dayCount}, 1fr);
  gap: 4px;
  pointer-events: none;
  z-index: 1;
`;

const DayHoverCell = styled.div<{ $hovered: boolean }>`
  position: relative;
  pointer-events: auto;
  cursor: pointer;
  &::before {
    content: '';
    position: absolute;
    inset: 3px;
    border-radius: 6px;
    border: ${props => props.$hovered ? `2px dashed ${V2Colours.indigo}` : 'none'};
  }
`;

const DayStatusOverlay = styled.div<{ $dayCount: number }>`
  position: absolute;
  inset: 4px 0;
  display: grid;
  grid-template-columns: repeat(${props => props.$dayCount}, 1fr);
  gap: 4px;
  pointer-events: none;
  z-index: 2;
`;

const DayStatusCell = styled.div`
  position: relative;
`;

const AddIconButton = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${V2Colours.indigo};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  box-shadow: 0 13px 11px 2px rgba(81, 91, 176, 0.09);
  .icon {
    font-size: 16px;
    font-weight: 900;
    width: auto;
    height: auto;
  }
`;

const ClickableCell = styled(Cell)`
  cursor: pointer;
  width: 100%;
`;

const SplitCell = styled.div`
  display: flex;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

const ClickableSplitCell = styled(SplitCell)`
  cursor: pointer;
  width: 100%;
`;

const CellLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${Colours.black50};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  .lds-ring div {
    border-color: ${Colours.lightBlue} transparent transparent transparent;
  }
`;

const CellFailedOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: ${Colours.black50};
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const SplitHalf = styled.div<{ $bg: string; $borderColour: string }>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 8px;
  background: ${props => props.$bg};
  border: 1px solid ${props => props.$borderColour};
  overflow: hidden;
  &:first-child {
    border-radius: 6px 0 0 6px;
    border-right: none;
  }
  &:last-of-type {
    border-radius: 0 6px 6px 0;
  }
`;
